import * as _ from "lodash";
import * as React from "react";
import * as S from "@styled";
import { connect } from "react-redux";

import { AgentGroup } from "../../../shared/model/AgentGroup";
import { DispatchType } from "../../../shared/utilities";
import { updateParameter } from "../../actions/UpdateParameter";
import { updateSelectedTypeId } from "../../actions/UpdateSelectedType";
import { IManualTriggerParameter, ISubProcessData } from "../../model/IProcessData";
import { ParameterEditorValue } from "../../model/ParameterEditorValue";
import { State } from "../../model/State";
import { ValidationError, ValidationErrorType } from "../../model/ValidationData";
import { remoteCallDictionaryName, remoteCallParameterName, timeoutMinutesParameterName } from "./CallActivityRemoteExecutionEditor";
import { RemoteExecutionEditor } from "./CallActivityRemoteExecutionEditor";
import { default as DescriptionEditor } from "../ElementDescriptionEditor";
import { hasValidationError } from "../Helpers";
import { default as LogResultEditor } from "../LogResultEditor";
import { default as ParameterInput } from "../ParameterInput";
import { default as PromoteResultEditor } from "../PromoteResultEditor";
import { SwitchCollapse } from "../SwitchCollapse";
import { default as TypeSelector } from "../TypeSelector";

export const CallActivityEditor: React.FC<CallActivityEditorProps> = (props) => {
    function getManualTriggerParameters() {
        if (props.manualTriggerParameters) {
            const usedParameters: string[] = [];
            const systemParameters = [remoteCallDictionaryName, remoteCallParameterName, timeoutMinutesParameterName];

            const parameters = props.manualTriggerParameters.map((parameter) => {
                let value: ParameterEditorValue = props.existingParameters[parameter.name];
                if (!value) {
                    value = { mode: "text", value: parameter.nonStringifiedDefaultValue || "" };
                    if (props.onValueMissing) {
                        props.onValueMissing(parameter.name, value);
                    }
                }

                usedParameters.push(parameter.name);

                return <ParameterInput
                    key={parameter.name}
                    name={parameter.name}
                    label={parameter.name}
                    value={value}
                    isValid={true}
                    elementId={props.id}
                    parameterSchema={parameter}
                />;
            });
            const unusedParameters = Object.keys(props.existingParameters)
                .filter((p) => usedParameters.indexOf(p) < 0 && systemParameters.indexOf(p) < 0);
            const obsoleteParameterEditors: JSX.Element[] = [];

            // tslint:disable-next-line:forin
            for (const key of unusedParameters) {
                const value = props.existingParameters[key];
                obsoleteParameterEditors.push(<ParameterInput
                    key={key}
                    name={key}
                    label={key}
                    value={value}
                    isValid={true}
                    elementId={props.id}
                    parameterSchema={{
                        inputType: "expression",
                        type: "dynamic",
                        name: key,
                        parameterType: "TextParameter",
                        defaultValue: "",
                        isSecret: false
                    }}
                />);
            }
            return <>
                <div>
                    {parameters}
                </div>
                {obsoleteParameterEditors.length > 0 && (
                    <div>
                        <hr />
                        <S.WarningContainer>
                            <div className="alert alert-info no-margin" role="alert">
                                {FRENDS.Localizations.ObsoleteSubprocessParameters}
                            </div>
                            {obsoleteParameterEditors}
                        </S.WarningContainer>
                    </div>
                )}
            </>;
        }
    }

    const handleSubProcessSelected = (subProcessId: string) => {
        if (subProcessId) {
            const schema = props.subProcessParamaters?.find((subProcess) => subProcess.uniqueIdentifier === subProcessId)?.manualTriggerParameters;
            if (props.onSubProcessSelected) {
                props.onSubProcessSelected(props.id, subProcessId, schema, props.existingParameters);
            }
        }
    };

    function getSubProcessDescription() {
        let description = "";

        const subProcess = props.subProcessParamaters?.find((sub) => sub.uniqueIdentifier === props.selectedTypeId);
        if (subProcess != null) {
            description = subProcess.description;
        }

        return description;
    }

    return (
        <div>
            {props.subProcessHasBeenDeleted && (
                <S.WarningContainer>
                    <div className="alert alert-warning" role="alert"><p>{FRENDS.Localizations.Warning} {FRENDS.Localizations.SubProcessHasBeenDeleted}</p>
                        <p>{props.selectedTypeId}</p>
                    </div>
                </S.WarningContainer>
            )}

            <TypeSelector
                placeholder={FRENDS.Localizations.SelectSubprocess}
                selectorModel={props.subProcessSelectorModel}
                id={props.id}
                onChange={handleSubProcessSelected}
                className={hasValidationError(props.validationErrors, ValidationErrorType.SubprocessSelectionMissing) ? "has-error" : null}
            />
            {props.selectedTypeId ?
                <div>
                    <div style={{ textAlign: "right" }}>
                        <S.OpenExternalLink target={"_blank"} href={`SubProcess/Edit/${props.selectedTypeId}`}>
                            {FRENDS.Localizations.OpenProcess} <i className="ci-external_link"></i>
                        </S.OpenExternalLink>
                    </div>
                    <SwitchCollapse
                        overflowOnExpandedOpenChildren={true}
                        isOpenByDefault={props.isAdvancedPanelOpenByDefault}
                        childrenCollapsed={
                            <DescriptionEditor id={props.id} defaultDescription={getSubProcessDescription()} showAsReadonly={true} />}
                        childrenOpen={
                            <div>
                                <DescriptionEditor id={props.id} defaultDescription={getSubProcessDescription()} />
                                <LogResultEditor elementId={props.id} />
                                <PromoteResultEditor id={props.id} />
                                <RemoteExecutionEditor {...props} />
                            </div>
                        }
                    />

                    <hr />
                    {getManualTriggerParameters()}
                </div> : null}
        </div>
    );
};

export interface CallActivityEditorProps {
    id?: string;
    selectedTypeId?: string;
    existingParameters?: any;
    manualTriggerParameters?: IManualTriggerParameter[];
    validationErrors?: ValidationError[];
    subProcessParamaters?: ISubProcessData[];
    subProcessSelectorModel?: any[];
    onSubProcessSelected?: (id: string | undefined, subProcessId: string, manualTriggerSchema: IManualTriggerParameter[] | undefined, existingParameters: any) => void;
    onValueMissing?: (key: string, value: any) => void;
    subProcessHasBeenDeleted?: boolean;
    isAdvancedPanelOpenByDefault?: boolean;
    agentGroups?: AgentGroup[];
}

const mapStateToProps = (state: State, ownProps: CallActivityEditorProps): CallActivityEditorProps => {
    let manualTriggerParameters: IManualTriggerParameter[] = [];
    let selectedTypeId: string | undefined;
    let isAdvancedPanelOpenByDefault = false;
    const element = state.elements[ownProps.id as string];
    let subProcessHasBeenDeleted = false;
    const agentGroups = state.agentGroups;
    const subProcessSelectorModel = state.subProcesses.filter((x) => x.uniqueIdentifier !== state.process.uniqueIdentifier).map((subProcess) => {
        return { value: subProcess.uniqueIdentifier, label: subProcess.name, title: subProcess.description };
    });

    if (element.selectedTypeId) {
        selectedTypeId = state.elements[ownProps.id as string].selectedTypeId;
        const foundSubProcess = state.subProcesses.find((subProcess) => subProcess.uniqueIdentifier === element.selectedTypeId);
        if (foundSubProcess) {
            manualTriggerParameters = foundSubProcess.manualTriggerParameters;
        } else {
            subProcessHasBeenDeleted = true;
        }
        isAdvancedPanelOpenByDefault = element.shouldNotLogResult || (_.has(state.elements[ownProps.id as string], "promoteResultAs") && state.elements[ownProps.id as string].promoteResultAs !== null) ||
            (_.has(state.elements[ownProps.id as string].parameters, remoteCallParameterName) && _.get(state.elements[ownProps.id as string].parameters, remoteCallParameterName));
    }

    return {
        subProcessSelectorModel: subProcessSelectorModel,
        manualTriggerParameters: manualTriggerParameters,
        subProcessParamaters: state.subProcesses,
        validationErrors: state.validation.validationDictionary[ownProps.id as string],
        selectedTypeId: selectedTypeId,
        subProcessHasBeenDeleted: subProcessHasBeenDeleted,
        isAdvancedPanelOpenByDefault: isAdvancedPanelOpenByDefault,
        agentGroups: agentGroups
    };
};

const mapDispatchToProps = (dispatch: DispatchType<State>, settings: CallActivityEditorProps): CallActivityEditorProps => {
    return {
        onSubProcessSelected: (id, taskId, manualTriggerSchema, existingParameters) => dispatch(updateSelectedTypeId(id, taskId, manualTriggerSchema, existingParameters)),
        onValueMissing: (key: string, value: any) => dispatch(updateParameter(settings.id, key, value)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(CallActivityEditor);
